import { useState } from 'react'
import { getSafeFileNameWithDate } from '../../lib/utils/files/generateFileName'
import { useCreateFolder } from '../api/documentHooks'
import { TOAST_IDS } from '../../lib/utils/toastCaller'

interface SafeCreateFolderParams {
	name: string
	parentId: string
	invalidate?: boolean
	autoRename?: boolean
}

export const useSafeCreateFolder = () => {
	const { mutateAsync: createFolder, isPending: isLoading } = useCreateFolder()

	const [sugName, setSugName] = useState('')

	const create = async ({
		name,
		parentId,
		autoRename = false,
	}: SafeCreateFolderParams) => {
		let currentName = name
		const toastOptions = autoRename
			? { toastId: TOAST_IDS.UPLOAD, updateToast: true }
			: {}

		try {
			const result = await createFolder(
				{
					data: { name: currentName, parentId },
					invalidate: false,
					...toastOptions,
				},
				{
					onError: (e: any) => {
						if (e?.response?.data?.data?.name) {
							currentName = e?.response?.data?.data?.name
							setSugName(e?.response?.data?.data?.name)
						}
					},
				},
			)

			if (!result?.data?.data?.id) throw new Error('Missing folder ID')

			return {
				id: result.data.data.id,
				manualRenameRequired: false,
				name: currentName,
			}
		} catch (error: any) {
			if (autoRename) {
				const retryName =
					currentName === name
						? getSafeFileNameWithDate(name, true, true)
						: currentName
				try {
					const retryResult = await createFolder(
						{
							data: { name: retryName, parentId },
							invalidate: false,
							...toastOptions,
						},
						{
							onSettled: () => {
								currentName = ''
							},
						},
					)
					if (retryResult?.data?.data?.id) {
						return {
							id: retryResult.data.data.id,
							manualRenameRequired: false,
							name: retryName,
						}
					}
				} catch (finalError) {
					// fall through to manualRenameRequired
				}
			}
			return {
				id: null as string | null,
				manualRenameRequired: true,
				originalName: name,
				suggestedName: currentName,
				parentId,
				error,
				name,
			}
		}
	}

	return { createSafeFolder: create, isLoading }
}
